import type { ImageProps } from "next/image"

import { getFullName } from "@bounce/util"
import type { NonNullListQueryNode } from "@bounce/util"

import type { Maybe, ReviewsQuery } from "../libs/bounce/bounceSdk.types.gen"
import { generateMockedUsers } from "../libs/bounce/reviews/generateMockedUsers"
import type { ReviewUser } from "../libs/bounce/reviews/generateMockedUsers"

export type Review = {
  id: string
  user: {
    image?: ImageProps["src"] | null
    fullName: string
  }
  city: {
    translatedName: string
    slug: string
  }
  storeId: string
  feedback: string | null
  translatedFeedback: string | null
  rating: number
  completedAt: string
  country?: string
  externalSource?: string | null
}

export type ReviewQueryNode = NonNullListQueryNode<ReviewsQuery["reviews"]>

export const getReviewUserFullName = (user: ReviewQueryNode["user"]) =>
  !user.lastName ? getFullName(user) : getFullName({ ...user, lastName: `${user.lastName.charAt(0)}.` })

export const isCompletedReview = <
  TReview extends {
    completedAt: Maybe<string>
    rating: Maybe<number>
  },
>(
  review: TReview,
): review is {
  completedAt: string
  rating: number
} & TReview => {
  return !!review.completedAt && typeof review.rating === "number"
}

export const mapReviewsWithMockedUsers = async (reviews: Review[], locale: string): Promise<Review[]> => {
  const mockedReviewUsers: ReviewUser[] = await generateMockedUsers(reviews.length, locale)

  return reviews.map((review, index) => {
    if (review.externalSource) {
      return review
    }

    const { fullName, image } = mockedReviewUsers[index] as ReviewUser

    return {
      ...review,
      user: {
        ...review.user,
        fullName,
        image: image ?? null,
      },
    }
  })
}
